import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Loader2, LogOut, User } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { useDiagramStore } from "../../store/diagramStore";
import { LoginForm } from "./LoginForm";
import toast from "react-hot-toast";
import { useTheme } from "../../context/ThemeContext";

interface AuthWrapperProps {
  children: React.ReactNode;
}

const CenterScreen = styled.div<{ $isDarkMode: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$isDarkMode ? "#1a1d23" : "#f9fafb")};
`;

const UserBarContainer = styled.div<{ $isDarkMode: boolean }>`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1000;
`;

const UserBar = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${(p) => (p.$isDarkMode ? "#2c2f36" : "#ffffff")};
  color: ${(p) => (p.$isDarkMode ? "#cbd5e1" : "#374151")};
  border: 1px solid ${(p) => (p.$isDarkMode ? "#3a3f47" : "#e5e7eb")};
  box-shadow: 0 8px 20px rgba(0, 0, 0, ${(p) => (p.$isDarkMode ? 0.35 : 0.08)});
  border-radius: 10px;
  padding: 8px 12px;
`;

const RoleBadge = styled.span`
  background: #dbeafe;
  color: #1e40af;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 9999px;
`;

const Text = styled.p<{ $muted?: boolean }>`
  margin: 0;
  font-size: 14px;
  color: ${(p) => (p.$muted ? "#6b7280" : "inherit")};
`;

const LogoutButton = styled.button<{ $isDarkMode: boolean }>`
  background: transparent;
  border: none;
  color: ${(p) => (p.$isDarkMode ? "#94a3b8" : "#6b7280")};
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s ease;
  &:hover {
    color: #dc2626;
  }
`;

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, checkAuth, logout } = useAuthStore();
  const { loadDiagrams } = useDiagramStore();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDiagrams();
    }
  }, [isAuthenticated, loadDiagrams]);

  const handleLoginSuccess = (user: any) => {
    toast.success(`Welcome back, ${user.username}!`);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Error logging out");
    }
  };

  if (isLoading) {
    return (
      <CenterScreen $isDarkMode={isDarkMode}>
        <div style={{ textAlign: "center" }}>
          <Loader2 className="animate-spin" style={{ width: 32, height: 32, margin: "0 auto 12px", color: "#2563eb" }} />
          <Text $muted>Loading...</Text>
        </div>
      </CenterScreen>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div style={{ position: "relative" }}>
      <UserBarContainer $isDarkMode={isDarkMode}>
        <UserBar $isDarkMode={isDarkMode}>
          <User style={{ width: 16, height: 16 }} />
          <Text>{user?.username || "Authenticated"}</Text>
          {user?.roles?.includes("admin") && <RoleBadge>Admin</RoleBadge>}
          <LogoutButton onClick={handleLogout} title="Logout" $isDarkMode={isDarkMode}>
            <LogOut style={{ width: 16, height: 16 }} />
          </LogoutButton>
        </UserBar>
      </UserBarContainer>

      {children}
    </div>
  );
};
